import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { ModalProps } from './Modal.types';

export const Modal = ({
  id,
  size,
  description,
  header,
  footer,
  footerAlign = 'left',
  isAnimated = false,
  children,
}: ModalProps): JSX.Element => {
  const ref = useRef();
  const [isModalOpen, setModalOpen] = useState(false);
  const show = () => {
    setModalOpen(true);
    document.body.classList.add('is-modal-open');
  };
  const hiding = () => {
    document.body.classList.remove('is-modal-open');
    document.body.classList.add('is-modal-closing');
    setTimeout(() => {
      document.body.classList.remove('is-modal-closing');
      setModalOpen(false);
    }, 400);
  };
  const hide = () => {
    document.body.classList.remove('is-modal-open');
    setModalOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const useOnClickOutside = (ref: any, handler: any) => {
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const listener = (event: any) => {
        if (event.target == ref?.current) {
          isAnimated ? hiding() : hide();
          return;
        }

        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener('click', listener);
      return () => {
        document.removeEventListener('click', listener);
      };
    }, [ref, handler]);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useOnClickOutside(ref, (event: any) => {
    event.target.id == id ? show() : null;
  });

  return (
    <div
      ref={ref}
      className={clsx('modal', 'modal_centered', {
        modal_small: size == 'Small',
        'modal_full-screen': size == 'Full',
        modal_animated: isAnimated,
      })}
      role='dialog'
      aria-modal='true'
      aria-hidden='false'
      style={isModalOpen ? { display: 'block' } : { display: 'none' }}
      data-testid='modal-trigger'
    >
      <div
        className='modal__container modal__container_scrollable'
        role='document'
      >
        {description && <div className='sr-only'>{description}</div>}
        <div className='modal__content'>
          <div className='modal__eyebrow'>
            <Button
              onClick={isAnimated ? hiding : hide}
              className='button button-close button-close_round button-close_large modal__close-button'
              testId='modal-close-btn'
            >
              <Icon icon='CloseX24' color='dark' size='large' />
            </Button>
          </div>

          {header && (
            <div className='modal__header' data-testid='modal-trigger-inner'>
              <h2 className='modal__title' id='modal_1-title'>
                {header}
              </h2>
            </div>
          )}

          <div className='modal__body'>{children}</div>

          {footer && (
            <>
              <div className='modal__divider divider' />
              <div
                className={clsx('modal__actions', {
                  'modal__actions-right': footerAlign === 'right',
                })}
              >
                {footer}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
